import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import { Settings as SettingsIcon, Save, User, Building, Bell, Shield, Database, Mail } from "lucide-react";
import { useToast } from "../hooks/use-toast";

export default function Settings() {
  const { toast } = useToast();
  const [settings, setSettings] = useState({
    // Site Configuration
    siteName: "Main Weighbridge",
    timezone: "UTC",
    weighUnit: "kg",
    
    // User Preferences
    theme: "light",
    notifications: true,
    emailAlerts: true,
    
    // System Settings
    autoBackup: true,
    sessionTimeout: "30",
    
    // Integration Settings
    navig8Connected: true,
    sendgridConfigured: false
  });

  const handleSave = () => {
    toast({
      title: "Settings Saved",
      description: "Your settings have been updated successfully.",
    });
  };

  const handleInputChange = (key: string, value: string | boolean) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="p-6 space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-[hsl(187,100%,42%)]">Settings</h1>
              <p className="text-muted-foreground mt-2">Manage system configuration and preferences</p>
            </div>
            
            <Button onClick={handleSave} className="bg-[hsl(24.6,95%,53%)] hover:bg-[hsl(24.6,95%,48%)] text-white" data-testid="button-save-settings">
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </div>

          <div className="grid gap-6">
        {/* Site Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[hsl(187,100%,42%)]">
              <Building className="w-5 h-5" />
              Site Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="siteName" className="text-[hsl(187,100%,42%)]">Site Name</Label>
                <Input
                  id="siteName"
                  value={settings.siteName}
                  onChange={(e) => handleInputChange("siteName", e.target.value)}
                  data-testid="input-site-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="timezone" className="text-[hsl(187,100%,42%)]">Timezone</Label>
                <Select value={settings.timezone} onValueChange={(value) => handleInputChange("timezone", value)}>
                  <SelectTrigger data-testid="select-timezone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTC">UTC</SelectItem>
                    <SelectItem value="America/New_York">Eastern Time</SelectItem>
                    <SelectItem value="America/Chicago">Central Time</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                    <SelectItem value="Europe/London">GMT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="weighUnit" className="text-[hsl(187,100%,42%)]">Weight Unit</Label>
                <Select value={settings.weighUnit} onValueChange={(value) => handleInputChange("weighUnit", value)}>
                  <SelectTrigger data-testid="select-weigh-unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">Kilograms (kg)</SelectItem>
                    <SelectItem value="lbs">Pounds (lbs)</SelectItem>
                    <SelectItem value="tons">Metric Tons</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* User Preferences */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[hsl(187,100%,42%)]">
              <User className="w-5 h-5" />
              User Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="theme" className="text-[hsl(187,100%,42%)]">Theme</Label>
                <Select value={settings.theme} onValueChange={(value) => handleInputChange("theme", value)}>
                  <SelectTrigger data-testid="select-theme">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">Light</SelectItem>
                    <SelectItem value="dark">Dark</SelectItem>
                    <SelectItem value="auto">Auto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="sessionTimeout" className="text-[hsl(187,100%,42%)]">Session Timeout (minutes)</Label>
                <Input
                  id="sessionTimeout"
                  type="number"
                  value={settings.sessionTimeout}
                  onChange={(e) => handleInputChange("sessionTimeout", e.target.value)}
                  data-testid="input-session-timeout"
                />
              </div>
            </div>
            
            <Separator />
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base text-[hsl(24.6,95%,53%)]">Push Notifications</Label>
                  <p className="text-sm text-muted-foreground">Receive notifications for important events</p>
                </div>
                <Switch
                  checked={settings.notifications}
                  onCheckedChange={(checked) => handleInputChange("notifications", checked)}
                  data-testid="switch-notifications"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base text-[hsl(24.6,95%,53%)]">Email Alerts</Label>
                  <p className="text-sm text-muted-foreground">Send email notifications for critical alerts</p>
                </div>
                <Switch
                  checked={settings.emailAlerts}
                  onCheckedChange={(checked) => handleInputChange("emailAlerts", checked)}
                  data-testid="switch-email-alerts"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* System Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[hsl(187,100%,42%)]">
              <Database className="w-5 h-5" />
              System Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base text-[hsl(24.6,95%,53%)]">Automatic Backup</Label>
                <p className="text-sm text-muted-foreground">Automatically backup data daily</p>
              </div>
              <Switch
                checked={settings.autoBackup}
                onCheckedChange={(checked) => handleInputChange("autoBackup", checked)}
                data-testid="switch-auto-backup"
              />
            </div>
          </CardContent>
        </Card>

        {/* Integration Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[hsl(187,100%,42%)]">
              <Shield className="w-5 h-5" />
              Integration Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div>
                  <Label className="text-base text-[hsl(24.6,95%,53%)]">Navig8 Services</Label>
                  <p className="text-sm text-muted-foreground">Authentication and master data</p>
                </div>
              </div>
              <Badge variant={settings.navig8Connected ? "default" : "destructive"}>
                {settings.navig8Connected ? "Connected" : "Disconnected"}
              </Badge>
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-muted-foreground" />
                <div>
                  <Label className="text-base text-[hsl(24.6,95%,53%)]">SendGrid Email</Label>
                  <p className="text-sm text-muted-foreground">Email notifications and alerts</p>
                </div>
              </div>
              <Badge variant={settings.sendgridConfigured ? "default" : "secondary"}>
                {settings.sendgridConfigured ? "Configured" : "Not Configured"}
              </Badge>
            </div>
          </CardContent>
        </Card>
          </div>
    </div>
  );
}